
import { Component, computed, input, InputSignal, output, OutputEmitterRef, Signal, ChangeDetectionStrategy } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { MatButtonModule } from '@angular/material/button';
import { MatMenuModule } from '@angular/material/menu';
import { IconComponent } from '@libs/components/icons';
import { ToggleNavigationComponent } from '@libs/components/navigation';
import { NavbarNavigationName, NavItemModel, NavModel } from '@libs/shared/models';
import { NavigationService, PageViewService } from '@libs/shared/services';
import { MobileNavigationSkeletonComponent } from './mobile-navigation-skeleton';

@Component({
  selector: 'ui-mobile-navigation',
  imports: [
    MatMenuModule,
    MatButtonModule,
    ToggleNavigationComponent,
    MobileNavigationSkeletonComponent,
    IconComponent,
  ],
  templateUrl: './mobile-navigation.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './mobile-navigation.component.scss',
})
export class MobileNavigationComponent {
  public categoryChanged: OutputEmitterRef<NavItemModel> = output();
  public currentCategoryId: InputSignal<string | undefined> = input();
  public view: InputSignal<NavbarNavigationName> = input<NavbarNavigationName>('main');
  public showTiles: Signal<boolean> = computed(() => this.view() === 'main');

  public readonly categoryMap: Signal<Map<string, NavItemModel>> = toSignal(this.pageViewService.getCategoryMap(), { initialValue: new Map() });

  public navigation: Signal<NavModel | undefined> = toSignal(this.navigationService.getMainNavigation());
  public navigationRoot: Signal<NavItemModel | undefined> = computed(() => {
    switch (this.view()) {
      case 'offers':
        return this.navigation()?.children?.[0];
      case 'main':
        return this.navigation();
      default:
        return undefined;
    }
  });

  public currentCategory: Signal<NavItemModel | undefined> = computed(() => {
    if (!this.navigationRoot()) {
      return undefined;
    }

    if (!this.currentCategoryId() || this.navigationRoot()?.id === this.currentCategoryId()) {
      return this.navigationRoot();
    }

    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    const item: NavItemModel | undefined = this.categoryMap().get(this.currentCategoryId()!);

    if (!item) {
      return this.navigationRoot();
    }

    return item;
  });

  public currentCategoryLabel: Signal<string> = computed(() => {
    const item: NavItemModel | undefined = this.currentCategory();

    if (!item?.label) {
      return 'Sortiment';
    }

    return item.label;
  });

  public constructor(
    private readonly navigationService: NavigationService,
    private readonly pageViewService: PageViewService,
  ) {
  }

  public async close(): Promise<boolean | void> {
    return this.navigationService.closeOverlayRoute();
  }
}
